import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import api from './api/client';
import { useAuth } from './context/AuthContext';
import { WarehouseProvider } from './context/WarehouseContext';
import { CustomerAuthProvider, useCustomerAuth } from './context/CustomerAuthContext';
import { CartProvider, useCart } from './context/CartContext';
import CatalogPage from './pages/CatalogPage';
import PageTransition from './components/PageTransition';
import ThemeToggle from './components/ThemeToggle';
import ContactIcon from './components/ContactIcon';
import CookieBanner from './components/CookieBanner';

// Ленивая загрузка: посетитель каталога не качает код админки и наоборот
const ModelDetailPage = lazy(() => import('./pages/ModelDetailPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderStatusPage = lazy(() => import('./pages/OrderStatusPage'));
const CustomerLogin = lazy(() => import('./pages/customer/CustomerLogin'));
const CustomerRegister = lazy(() => import('./pages/customer/CustomerRegister'));
const CustomerOrders = lazy(() => import('./pages/customer/CustomerOrders'));
const CustomerForgot = lazy(() => import('./pages/customer/CustomerForgot'));
const CustomerReset = lazy(() => import('./pages/customer/CustomerReset'));
const CustomerActivate = lazy(() => import('./pages/customer/CustomerActivate'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminModels = lazy(() => import('./pages/admin/AdminModels'));
const AdminSupplies = lazy(() => import('./pages/admin/AdminSupplies'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminWarehouses = lazy(() => import('./pages/admin/AdminWarehouses'));
const AdminCash = lazy(() => import('./pages/admin/AdminCash'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminInvoices = lazy(() => import('./pages/admin/AdminInvoices'));
const AdminInvoiceView = lazy(() => import('./pages/admin/AdminInvoiceView'));
const AdminDocumentView = lazy(() => import('./pages/admin/AdminDocumentView'));
const SellerDashboard = lazy(() => import('./pages/seller/SellerDashboard'));
const SellerSettings = lazy(() => import('./pages/seller/SellerSettings'));
const LegalPage = lazy(() => import('./pages/LegalPage'));

const BRAND_TYPES = new Set(['whatsapp', 'telegram', 'instagram', 'youtube', 'tiktok', 'vk', 'facebook']);

function App() {
  const { admin, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const isAdmin = location.pathname.startsWith('/admin');
  const isSeller = location.pathname.startsWith('/seller');
  const isPanel = isAdmin || isSeller;
  const [siteSettings, setSiteSettings] = useState({ site_name: '3D Print Shop', logo_url: null });
  const [contacts, setContacts] = useState([]);

  const role = admin?.role || 'admin';

  useEffect(() => {
    api.get('/settings').then(r => setSiteSettings(r.data)).catch(() => {});
    api.get('/contacts').then(r => setContacts(r.data)).catch(() => {});
  }, [location.pathname]);

  if (loading) return <div className="loading">Загрузка...</div>;

  const panelLink = role === 'seller' ? '/seller' : '/admin';
  const panelLabel = role === 'seller' ? 'Продажи' : 'Админ';

  return (
    <CustomerAuthProvider>
    <CartProvider>
    <WarehouseProvider>
    <div className="app">
      <header className="header">
        <div className="container header-inner">
          <Link to="/" className="logo">
            {siteSettings.logo_url
              ? <img src={siteSettings.logo_url} alt="Logo" style={{ height: 36, borderRadius: 4 }} />
              : <span className="logo-icon">&#9883;</span>}
            {' '}{siteSettings.site_name}
          </Link>
          <nav className="nav">
            <Link to="/" className={!isPanel ? 'active' : ''}>Каталог</Link>
            <Link to="/order-status" className="hide-mobile">Проверить заказ</Link>
            <CartLink />
            <AccountNav admin={admin} panelLink={panelLink} panelLabel={panelLabel} isPanel={isPanel} onLogout={handleLogout} />
            <ThemeToggle />
          </nav>
        </div>
      </header>
      <PageTransition><Suspense fallback={<div className="loading">Загрузка...</div>}><Routes>
        <Route path="/" element={<CatalogPage />} />
        <Route path="/model/:id" element={<ModelDetailPage />} />
        <Route path="/login" element={<AdminGuestOnly admin={admin} role={role}><LoginPage /></AdminGuestOnly>} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/order-status" element={<OrderStatusPage />} />
        <Route path="/order-status/:orderNumber" element={<OrderStatusPage />} />
        <Route path="/order-success/:orderNumber" element={<OrderStatusPage successMode />} />
        <Route path="/account/login" element={<CustomerGuestOnly><CustomerLogin /></CustomerGuestOnly>} />
        <Route path="/account/register" element={<CustomerGuestOnly><CustomerRegister /></CustomerGuestOnly>} />
        <Route path="/account/orders" element={<CustomerOrders />} />
        <Route path="/account/forgot" element={<CustomerGuestOnly><CustomerForgot /></CustomerGuestOnly>} />
        <Route path="/account/reset" element={<CustomerGuestOnly><CustomerReset /></CustomerGuestOnly>} />
        <Route path="/account/activate" element={<CustomerActivate />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        {admin && role === 'admin' && (<>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/models" element={<AdminModels />} />
          <Route path="/admin/supplies" element={<AdminSupplies />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/cash" element={<AdminCash />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/invoices" element={<AdminInvoices />} />
          <Route path="/admin/invoices/:id" element={<AdminInvoiceView />} />
          <Route path="/admin/invoices/acts/:id" element={<AdminDocumentView kindName="acts" />} />
          <Route path="/admin/invoices/waybills/:id" element={<AdminDocumentView kindName="waybills" />} />
          <Route path="/admin/warehouses" element={<AdminWarehouses />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/seller" element={<SellerDashboard />} />
        </>)}
        {admin && role === 'seller' && (<>
          <Route path="/seller" element={<SellerDashboard />} />
          <Route path="/seller/warehouses" element={<AdminWarehouses />} />
          <Route path="/seller/settings" element={<SellerSettings />} />
          <Route path="/admin/*" element={<Navigate to="/seller" replace />} />
        </>)}
      </Routes></Suspense></PageTransition>
      <footer className="footer">
        {!isPanel && <SiteFooter contacts={contacts} admin={admin} panelLink={panelLink} panelLabel={panelLabel} />}
        <div>{siteSettings.site_name} &copy; {new Date().getFullYear()}</div>
      </footer>
      {!isPanel && <CookieBanner />}
    </div>
    </WarehouseProvider>
    </CartProvider>
    </CustomerAuthProvider>
  );
}

function CartLink() {
  const { totalCount } = useCart();
  return (
    <Link to="/cart" className="nav-cart">🛒{totalCount > 0 && <span className="nav-cart-badge">{totalCount}</span>}</Link>
  );
}

function SiteFooter({ contacts, admin, panelLink, panelLabel }) {
  const { customer } = useCustomerAuth();
  return (
    <div className="footer-grid">
      <div className="footer-col">
        <h4>Магазин</h4>
        <Link to="/">Каталог</Link>
        <Link to="/cart">Корзина</Link>
        <Link to="/order-status">Проверить заказ</Link>
      </div>
      <div className="footer-col">
        <h4>{admin ? 'Управление' : 'Личный кабинет'}</h4>
        {admin ? (
          <Link to={panelLink}>{panelLabel}</Link>
        ) : customer ? (
          <Link to="/account/orders">Мои заказы</Link>
        ) : (<>
          <Link to="/account/login">Вход</Link>
          <Link to="/account/register">Регистрация</Link>
        </>)}
      </div>
      {contacts.length > 0 && (
        <div className="footer-col">
          <h4>Контакты</h4>
          {contacts.map(c => (
            <a key={c.id} href={c.value.startsWith('http') || c.value.startsWith('mailto:') || c.value.startsWith('tel:') ? c.value : (c.type === 'phone' ? `tel:${c.value}` : c.type === 'email' ? `mailto:${c.value}` : c.value)} target={c.value.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" title={c.label} className="footer-contact-row">
              <ContactIcon type={c.type} brand={BRAND_TYPES.has(c.type)} />
              <span>{c.label}</span>
            </a>
          ))}
        </div>
      )}
      <div className="footer-col">
        <h4>Документы</h4>
        <Link to="/legal/privacy">Политика конфиденциальности</Link>
        <Link to="/legal/terms">Публичная оферта</Link>
        <Link to="/legal/consent">Согласие на обработку данных</Link>
      </div>
    </div>
  );
}

function AdminGuestOnly({ admin, role, children }) {
  if (admin) return <Navigate to={role === 'seller' ? '/seller' : '/admin'} replace />;
  return children;
}

function CustomerGuestOnly({ children }) {
  const { customer, loading } = useCustomerAuth();
  if (loading) return null;
  if (customer) return <Navigate to="/account/orders" replace />;
  return children;
}

function AccountNav({ admin, panelLink, panelLabel, isPanel, onLogout }) {
  const { customer, logout: customerLogout } = useCustomerAuth();
  if (admin) return (<>
    <Link to={panelLink} className={isPanel ? 'active' : ''}>{panelLabel}</Link>
    <button onClick={onLogout} className="hide-mobile">Выйти</button>
  </>);
  if (customer) return (<>
    <Link to="/account/orders" title="Мои заказы"><span className="hide-mobile">Мои заказы</span><span className="show-mobile" aria-hidden="true">👤</span></Link>
    <button onClick={customerLogout} className="hide-mobile">Выйти</button>
  </>);
  return <Link to="/login">Войти</Link>;
}

export default App;
